import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  Textarea,
  Tooltip,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MutationParams,
  ThreadPayload,
} from '@/patient-app-common/types/SecureMessageThreads';
import { useUpdateSecureMessageThreadMutation } from '@/patient-app-common/api/secureMessageApi';

import { ImageThumbnail } from './ImagePreview';
import { PdfAttachment } from './PdfPreview';

interface ThreadInputProps {
  replyMode: boolean;
  setReplyMode: (boolean: boolean) => void;
  setSidebarOverlaps: (boolean: boolean) => void;
  thread: undefined | ThreadPayload;
}

export function ThreadInput({
  replyMode,
  setReplyMode,
  setSidebarOverlaps,
  thread,
}: ThreadInputProps) {
  const { t } = useTranslation('inbox');

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [updateThread, { isLoading, isError }] =
    useUpdateSecureMessageThreadMutation();

  const [modal, toggleModal] = useState<boolean>(false);
  const [text, setText] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);

  const disableReply =
    thread?.resolved || thread?.patientReplyEnabled === false;

  useEffect(() => {
    if (replyMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [replyMode]);

  const handleSubmit = async () => {
    if (!thread) return;

    const params: MutationParams = {
      text,
    };

    if (file) {
      params.file = file;
    }

    params.secureMessageThreadId = thread.id.toString();
    const result = await updateThread(params).unwrap();
    if (result.success) {
      setReplyMode(false);
      setText('');
      setFile(null);
    }
  };

  const handleDelete = () => {
    setReplyMode(false);
    setText('');
    setFile(null);
    toggleModal(false);
  };

  const footerText = useMemo(() => {
    if (!thread) return '';

    let text = '';

    if (thread.resolved) {
      text = t('thread.footer_resolved');
    } else if (!thread.patientReplyEnabled) {
      text = t('thread.footer_disabled');
    }

    return text;
  }, [thread]);

  if (disableReply) {
    return (
      <>
        <div className="bottom-0 sticky sm:block md:hidden m-4">
          <Tooltip content={t('common.action.back')}>
            <Button
              isIconOnly
              color="primary"
              variant="faded"
              onPress={() => setSidebarOverlaps(true)}
            >
              <Icon
                icon="tabler:layout-sidebar-left-collapse-filled"
                className="text-2xl"
              />
            </Button>
          </Tooltip>
        </div>
        <div className="bg-warning-200 text-center p-2">
          <p className="font-bold text-warning-900">{footerText}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <ExitAlert
        isOpen={modal}
        onClose={() => toggleModal(false)}
        handleDelete={handleDelete}
      />
      <div className="bottom-0 sticky m-4 bg-transparent flex-none">
        {replyMode && (
          <Textarea
            className="w-full mb-4"
            color="primary"
            classNames={{
              inputWrapper: 'bg-background',
            }}
            errorMessage={t('threadinput.error')}
            isInvalid={isError}
            label={t('common.input.text_placeholder')}
            maxLength={300}
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              setText(e.currentTarget.value)
            }
            ref={inputRef}
            value={text}
            variant="bordered"
          />
        )}
        {file && file.type.includes('image') && (
          <ImageThumbnail file={file} setFile={setFile} />
        )}
        {file && file.type.includes('pdf') && (
          <PdfAttachment file={file} setFile={setFile} />
        )}
        <div className="flex mt-4 justify-between">
          <div className="flex">
            <div className="sm:block md:hidden mr-4">
              <Tooltip content={t('common.action.back')}>
                <Button
                  isIconOnly
                  color="primary"
                  variant="faded"
                  onPress={() => setSidebarOverlaps(true)}
                >
                  <Icon
                    icon="tabler:layout-sidebar-left-collapse-filled"
                    className="text-2xl"
                  />
                </Button>
              </Tooltip>
            </div>
            {replyMode && (
              <>
                <input
                  id="upload"
                  type="file"
                  accept="image/*, application/pdf"
                  onChange={(e) => {
                    if (e.target?.files?.length) {
                      setFile(e.target.files[0]);
                    }
                  }}
                  ref={fileInput}
                  className="hidden"
                />
                <Tooltip content={t('common.action.attach')}>
                  <Button
                    isIconOnly
                    className="mr-4"
                    color="primary"
                    variant="faded"
                    onPress={() => {
                      if (fileInput.current) {
                        fileInput.current.click();
                      }
                    }}
                  >
                    <Icon icon="tabler:photo-plus" className="text-2xl" />
                  </Button>
                </Tooltip>
                <Tooltip content={t('threadinput.button.delete')}>
                  <Button
                    isDisabled={!text.length}
                    isIconOnly
                    color="primary"
                    variant="faded"
                    onPress={() => toggleModal(true)}
                  >
                    <Icon icon="tabler:trash-x" className="text-2xl" />
                  </Button>
                </Tooltip>
              </>
            )}
          </div>
          {replyMode ? (
            <Button
              color="primary"
              isLoading={isLoading}
              variant="solid"
              onPress={handleSubmit}
            >
              <span>{t('common.action.send')}</span>
              <Icon icon="tabler:send-2" />
            </Button>
          ) : (
            <Button
              color="primary"
              onPress={() => {
                setReplyMode(true);
                inputRef.current?.focus();
              }}
            >
              <span>{t('threadinput.button.reply')}</span>
              <Icon icon="tabler:message-reply" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

interface ExitAlertProps {
  isOpen: boolean;
  onClose: () => void;
  handleDelete: () => void;
}

function ExitAlert({ isOpen, onClose, handleDelete }: ExitAlertProps) {
  const { t } = useTranslation('inbox');

  return (
    <Modal hideCloseButton isOpen={isOpen} onClose={onClose}>
      <ModalContent>
        <ModalBody className="pt-4">
          <p>{t('threadinput.exit_alert_text')}</p>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" variant="flat" onPress={handleDelete}>
            {t('threadinput.button.delete')}
          </Button>
          <Button color="primary" variant="bordered" onPress={onClose}>
            {t('common.action.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
